/**
 * Vehicle detail sub-components — ported from AJS directives.
 *
 * vehicle-attachments  → delegates to wor-attachments-box
 * vehicle-events       → wor-table backed by /vehicles/event_list
 * vehicle-invoices     → wor-table backed by /vehicles/invoice_list
 * vehicle-inspections  → wor-table backed by /vehicles/inspection_list
 */
import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UIRouter }         from '@uirouter/core';

import { ApiService }      from '../api/api.service';
import { CompanyService }  from '../companies/company.service';
import { CustomerModel }   from '../customers/models/customer';
import { TableRowEvent }   from '../table/types/table-row-event';
import { TableService }    from '../table/table.service';
import { VehicleModel }    from './models/vehicle';
import { EditComponent }   from '../table/cell-components/actions/edit.component';

// ---------------------------------------------------------------------------
// vehicle-attachments — thin wrapper around wor-attachments-box
// ---------------------------------------------------------------------------
@Component({
  selector: 'vehicle-attachments',
  template: `<wor-attachments-box [attachments]="attachments" [options]="options"></wor-attachments-box>` })
export class VehicleAttachmentsPlaceholderComponent {
  @Input() attachments: any;
  @Input() options: any;
}

// ---------------------------------------------------------------------------
// vehicle-events
// ---------------------------------------------------------------------------
@Component({
  selector: 'vehicle-events',
  template: `
    <div class="box">
      <div class="box-header">
        <h2><i class="fa fa-calendar"></i><span class="break"></span>{{ 'QUERY_DROPDOWN_SPACE.NAMES.VEHICLE_BOOKINGS' | translate }}</h2>
        <div class="box-icon" *ngIf="!vehicle?.archived">
          <a [title]="'TOOL_TIP_SPACE.CUSTOMERS.CREATE_BOOKING' | translate"
            uiSref="app.event" [uiParams]="{ customer_id: customer?.id, vehicle_id: vehicle?.id }">
            <i class="fa fa-plus"></i>
          </a>
        </div>
        <div class="box-icon">
          <a href="javascript:void(0)" (click)="collapsed = !collapsed"><i class="fa" [ngClass]="collapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i></a>
        </div>
      </div>
      <div class="box-content" [hidden]="collapsed">
        <wor-table
          [(data)]="events"
          (query)="getEvents($event.params)"
          [settings]="tableSettings"
          (rowDoubleClick)="onRowDoubleClick($event)">
        </wor-table>
      </div>
    </div>
  ` })
export class VehicleEventsPlaceholderComponent implements OnInit {
  @Input() vehicle: VehicleModel;
  @Input() events: any;
  @Input() customer: CustomerModel;

  collapsed = false;
  tableSettings: any;

  constructor(
    private api: ApiService,
    private router: UIRouter,
    private tableService: TableService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.collapsed = !this.events?.length;
    const cache = this.tableService.getCacheForTable('vehicleEvents');

    this.tableSettings = {
      cache: { enabled: true, key: 'vehicleEvents' },
      columns: [
        { field: 'event_number', header: { text: this.translate.instant('JS_SPACE.COLUMNS.BOOKING_NUMBER') }, classes: 'all-caps' },
        { field: 'event_date',   header: { text: this.translate.instant('BOOKING_SPACE.BOOKING_DATE') }, pipe: 'globalDate' },
        { field: 'event_status', header: { text: this.translate.instant('GENERAL_SPACE.FIELD.STATUS') }, pipe: 'invoiceStatus' },
        { field: 'description',  header: { text: this.translate.instant('GENERAL_SPACE.FIELD.DESCRIPTION') } },
        { field: 'total',        header: { text: this.translate.instant('GENERAL_SPACE.FIELD.TOTAL') }, pipe: 'globalCurrency' },
        { component: { bindings: { outputs: { onEditClick: (e: any) => this.viewEvent(e) } }, enabled: !this.vehicle?.archived, factory: EditComponent }, sortable: false },
      ],
      empty: { text: this.translate.instant('QUERY_DROPDOWN_SPACE.GRID.NO_BOOKINGS_FOUND') },
      pagination: {
        enabled: true,
        pageSize: cache.paging?.pageSize || 5,
        pageSizes: [5, 10, 25, 50],
        queryCount: () => this.api.get('/vehicles/event_count/' + this.vehicle.id) },
      rows: { highlight: { enabled: true } },
      sorting: { direction: cache.sorting?.direction || 'desc', field: cache.sorting?.field || 'event_date' } };
  }

  getEvents(params: any) {
    return this.api.get('/vehicles/event_list', [this.vehicle.id, params.offset, params.pageSize, params.field, params.direction])
      .then((events: any) => { this.events = events; return events; });
  }

  onRowDoubleClick(event: TableRowEvent) {
    if (!this.vehicle?.archived) this.viewEvent(event.row);
  }

  viewEvent(event: any) {
    this.router.stateService.go('app.event', { customer_id: this.customer.id, id: event.id, vehicle_id: event.vehicle_id });
  }
}

// ---------------------------------------------------------------------------
// vehicle-invoices
// ---------------------------------------------------------------------------
@Component({
  selector: 'vehicle-invoices',
  template: `
    <div class="box">
      <div class="box-header">
        <h2>
          <i class="fa fa-folder-open"></i><span class="break"></span>
          {{ (vehicle?.vehicle_group === 'MUSIC' ? 'QUERY_DROPDOWN_SPACE.NAMES.INSTRUMENT_INVOICES' : 'QUERY_DROPDOWN_SPACE.NAMES.VEHICLE_INVOICES') | translate }}
        </h2>
        <div class="box-icon" *ngIf="!vehicle?.archived">
          <a title="Create Invoice" uiSref="app.invoice" [uiParams]="{ customer_id: customer?.id, vehicle_id: vehicle?.id }">
            <i class="fa fa-plus"></i>
          </a>
        </div>
        <div class="box-icon">
          <a href="javascript:void(0)" (click)="collapsed = !collapsed"><i class="fa" [ngClass]="collapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i></a>
        </div>
      </div>
      <div class="box-content" [hidden]="collapsed">
        <wor-table
          [(data)]="invoices"
          (query)="getInvoices($event.params)"
          [settings]="tableSettings"
          (rowDoubleClick)="onRowDoubleClick($event)">
        </wor-table>
      </div>
    </div>
  ` })
export class VehicleInvoicesPlaceholderComponent implements OnInit {
  @Input() vehicle: VehicleModel;
  @Input() invoices: any;
  @Input() customer: CustomerModel;

  collapsed = false;
  tableSettings: any;

  constructor(
    private api: ApiService,
    public companyService: CompanyService,
    private router: UIRouter,
    private tableService: TableService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.collapsed = !this.invoices?.length;
    const cache = this.tableService.getCacheForTable('vehicleInvoices');

    const columns: any[] = [];

    if (this.companyService.isMultiBranch()) {
      columns.push({ field: 'store_name', header: { text: 'Store Name' } });
    }

    columns.push(
      { field: 'invoice_number', header: { text: this.translate.instant('INVOICE_SPACE.INVOICE_NUMBER') } },
      { field: 'post_date',      header: { text: this.translate.instant('GENERAL_SPACE.FIELD.POST_DATE') }, pipe: 'globalDate' },
      { field: 'description',    header: { text: this.translate.instant('GENERAL_SPACE.FIELD.DESCRIPTION') } },
      { field: 'invoice_type',   header: { text: this.translate.instant('JS_SPACE.COLUMNS.TRAN_TYPE') }, pipe: 'invoiceType' },
      { field: 'invoice_status', header: { text: this.translate.instant('GENERAL_SPACE.FIELD.STATUS') }, pipe: 'invoiceStatus' },
      { field: 'total',          header: { text: this.translate.instant('JS_SPACE.COLUMNS.AMOUNT') }, pipe: 'globalCurrency' },
      { field: 'balance_due',    header: { text: this.translate.instant('JS_SPACE.COLUMNS.BALANCE') }, pipe: 'globalCurrency' },
      { component: { bindings: { outputs: { onEditClick: (e: any) => this.viewInvoice(e) } }, enabled: !this.vehicle?.archived, factory: EditComponent }, sortable: false },
    );

    this.tableSettings = {
      cache: { enabled: true, key: 'vehicleInvoices' },
      columns,
      empty: { text: this.translate.instant('QUERY_DROPDOWN_SPACE.GRID.NO_INVOICES_FOUND') },
      pagination: {
        enabled: true,
        pageSize: cache.paging?.pageSize || 5,
        pageSizes: [5, 10, 20, 50],
        queryCount: () => this.api.get('/vehicles/invoice_count/' + this.vehicle.id) },
      rows: { highlight: { enabled: true } },
      sorting: { direction: cache.sorting?.direction || 'desc', field: cache.sorting?.field || 'post_date' } };
  }

  getInvoices(params: any) {
    return this.api.get('/vehicles/invoice_list', [this.vehicle.id, params.offset, params.pageSize, params.field, params.direction])
      .then((invoices: any) => { this.invoices = invoices; return invoices; });
  }

  onRowDoubleClick(event: TableRowEvent) {
    if (!this.vehicle?.archived) this.viewInvoice(event.row);
  }

  viewInvoice(invoice: any) {
    this.router.stateService.go('app.invoice', { customer_id: this.customer.id, id: invoice.id, vehicle_id: invoice.vehicle_id });
  }
}

// ---------------------------------------------------------------------------
// vehicle-inspections
// ---------------------------------------------------------------------------
@Component({
  selector: 'vehicle-inspections',
  template: `
    <div class="box">
      <div class="box-header">
        <h2><i class="fa fa-search"></i><span class="break"></span>{{ 'QUERY_DROPDOWN_SPACE.NAMES.VEHICLE_INSPECTIONS' | translate }}</h2>
        <div class="box-icon" *ngIf="!vehicle?.archived">
          <a title="Create Inspection" uiSref="app.inspection" [uiParams]="{ customer_id: customer?.id, vehicle_id: vehicle?.id }">
            <i class="fa fa-plus"></i>
          </a>
        </div>
        <div class="box-icon">
          <a href="javascript:void(0)" (click)="collapsed = !collapsed"><i class="fa" [ngClass]="collapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i></a>
        </div>
      </div>
      <div class="box-content" [hidden]="collapsed">
        <wor-table
          [(data)]="inspections"
          (query)="getInspections($event.params)"
          [settings]="tableSettings"
          (rowDoubleClick)="onRowDoubleClick($event)">
        </wor-table>
      </div>
    </div>
  ` })
export class VehicleInspectionsPlaceholderComponent implements OnInit {
  @Input() vehicle: VehicleModel;
  @Input() inspections: any;
  @Input() statuses: any;
  @Input() customer: CustomerModel;

  collapsed = false;
  tableSettings: any;

  constructor(
    private api: ApiService,
    private router: UIRouter,
    private tableService: TableService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.collapsed = !this.inspections?.length;
    const cache = this.tableService.getCacheForTable('vehicleInspections');

    this.tableSettings = {
      cache: { enabled: true, key: 'vehicleInspections' },
      columns: [
        { field: 'inspection_number', header: { text: this.translate.instant('JS_SPACE.COLUMNS.INSPECTION_NUMBER') } },
        { field: 'inspection_date',   header: { text: this.translate.instant('GENERAL_SPACE.FIELD.DATE') }, pipe: 'globalDate' },
        { field: 'description',       header: { text: this.translate.instant('GENERAL_SPACE.FIELD.DESCRIPTION') } },
        { field: 'inspection_status', header: { text: this.translate.instant('GENERAL_SPACE.FIELD.STATUS') }, pipe: 'inspectionStatus' },
        { component: { bindings: { outputs: { onEditClick: (e: any) => this.viewInspection(e) } }, enabled: true, factory: EditComponent }, sortable: false },
      ],
      empty: { text: this.translate.instant('INSPECTIONS_SPACE.NO_INSPECTIONS') },
      pagination: {
        enabled: true,
        pageSize: cache.paging?.pageSize || 5,
        pageSizes: [5, 10, 25, 50],
        queryCount: () => this.api.get('/vehicles/inspection_count', this.vehicle.id) },
      rows: { highlight: { enabled: true } },
      sorting: { direction: cache.sorting?.direction || 'asc', field: cache.sorting?.field || 'inspection_date' } };
  }

  getInspections(params: any) {
    return this.api.get('/vehicles/inspection_list', [this.vehicle.id, params.offset, params.pageSize, params.field, params.direction])
      .then((inspections: any) => { this.inspections = inspections; return inspections; });
  }

  onRowDoubleClick(event: TableRowEvent) {
    this.viewInspection(event.row);
  }

  viewInspection(inspection: any) {
    this.router.stateService.go('app.inspection', { customer_id: inspection.customer_id, vehicle_id: inspection.vehicle_id, id: inspection.id });
  }
}
